import img_loading from '@/../public/img/search/img_loading.png';
import {cn} from '@/lib/utils';
import Image from 'next/image';
import {useEffect, useRef, useState} from 'react';

interface LazyImageProps {
  src?: string;
  className?: string;
}

const LazyImage = ({
  src = img_loading.src,
  className = 'w-full h-full',
  ...options
}: LazyImageProps & any) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [inView, setInView] = useState(false);
  const [inError, setInError] = useState(false);
  const handleImageLoaded = () => {
    setTimeout(() => {
      setIsImageLoaded(true);
    }, 100);
  };

  // Simple gray placeholder (base64-encoded 1x1 pixel)
  const placeholderImage = img_loading.src;
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (imgRef.current && !isImageLoaded) {
            handleImageLoaded();
          }
          observer.disconnect();
        }
      },
      {threshold: 0.1}
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  });

  useEffect(() => {
    if (isImageLoaded) {
      setTimeout(() => {
        setOpacity(1);
      }, 500);
    }
  }, [isImageLoaded]);

  return (
    <img
      ref={imgRef}
      alt="banner"
      src={
        isImageLoaded && inView && !inError && src?.trim()
          ? src?.trim()
          : placeholderImage
      }
      className={cn(
        'transition-all duration-500 linear',
        ' object-cover object-center ',
        className,
        opacity ? 'opacity-100' : isImageLoaded ? 'opacity-0' : 'opacity-100',
        !isImageLoaded && 'animate-pulse '
      )}
      style={isImageLoaded ? {opacity} : {}}
      onLoad={() => {
        handleImageLoaded();
      }}
      onError={(e) => {
        handleImageLoaded();
        setInError(true);
      }}
      {...options}
    />
  );
};

const LazyNextImage = ({
  src,
  className = '',
  ...options
}: LazyImageProps & any) => {
  const [inView, setInView] = useState(false);

  // Simple gray placeholder (base64-encoded 1x1 pixel)
  const placeholderImage = img_loading.src;
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {threshold: 0.1}
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  });

  return (
    <Image
      ref={imgRef}
      src={inView ? src.trim() : placeholderImage}
      className={cn(
        className,
        !inView && 'animate-pulse object-cover object-center w-full h-full'
      )}
      {...options}
    />
  );
};

const Lazybg = ({src, className}: LazyImageProps & any) => {
  const [inView, setInView] = useState(false);

  // Simple gray placeholder (base64-encoded 1x1 pixel)
  const placeholderImage = img_loading.src;
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {threshold: 0.1}
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  });

  return (
    <div
      className={cn(className || 'w-full overflow-hidden bg-cover bg-center')}
      ref={imgRef}
      style={{
        backgroundImage: `url("${
          inView ? (src?.trim() ?? placeholderImage) : placeholderImage
        }")`
      }}
    ></div>
  );
};

export {Lazybg, LazyImage, LazyNextImage};
